import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'pounceV2';
import dayjs from 'dayjs';
import { useCancelAIInferenceStream } from 'Components/AIExplainLogEvents/graphql/cancelAiInferenceStream.generated';
import { useAIStreamManagementV3 } from 'Components/AIExplainLogEvents/hooks/useAIStreamManagementV3';
import { AISummarizeKind, OutputLength } from 'Generated/schema';
import { useAIConversation } from 'Pages/AI/graphql/aiConversation.generated';
import type { AIConversation } from 'Pages/AI/graphql/aiConversation.generated';
import { useCreateAIConversation } from 'Pages/AI/graphql/createAIConversation.generated';
import { useContinueAIConversation } from 'Pages/AI/graphql/continueAIConversation.generated';
import type { ConversationMessage } from '../MessageListV3';

interface UseConversationMessagingV3Props {
  conversationId: string | null;
  isPrivate: boolean;
  onConversationCreated: (id: string) => void;
  /** Optional alias to associate with a new conversation (e.g. alertId) */
  alias?: string;
  /** Optional kind to set on a new conversation (e.g. AISummarizeKind.Alert) */
  kind?: AISummarizeKind;
  /** Optional events to include when creating a new conversation (e.g. selected alerts as JSON) */
  events?: string[];
  /** Called after each stream finishes (complete or error) — use for external cache invalidation */
  onStreamComplete?: () => void;
  /** When false, skip the auto-scroll-to-bottom on conversation navigation. Default true. */
  autoScrollBottom?: boolean;
}

export interface UseConversationMessagingV3Return {
  // Conversation metadata
  conversation: AIConversation['aiConversation'] | null;

  // Messages
  messages: ConversationMessage[];
  hasMore: boolean;
  loadMore: () => void;

  // Loading states
  isStreaming: boolean;
  isCreating: boolean;
  isContinuing: boolean;
  isInitialLoad: boolean;
  isLoadingMore: boolean;

  // Current streaming state
  currentUserPrompt: string;
  isCancelled: boolean;

  // Actions
  handleSendMessage: (
    userPrompt: string,
    outputLength: OutputLength,
    attachments?: string[],
    displayPrompt?: string,
  ) => Promise<void>;
  cancelStream: () => void;
  reset: () => void;

  // Scroll control
  forceScrollToBottom: number;

  // Error
  error: Error | null;
}

/**
 * This hook manages messages from 3 sources, merging them into a single timeline:
 *
 * Server messages (source of truth)
 *    - Fetched via useAIConversation query
 *    - Contains historical messages with full metadata
 *    - Synced to local state in effect below (line ~154)
 *
 * Optimistic updates (temporary, for instant UX feedback)
 *    - User sends message → immediately add with tempId
 *    - Mutation returns → replace tempId with real streamId
 *    - Prevents UI lag while waiting for server
 *
 * Streaming message (real-time AI response)
 *    - Tracked by activeStreamId state
 *    - Updated in real-time via useAIStreamManagement hook
 *    - When finished → refetch to get final server version
 *
 * CRITICAL: When activeStreamId is set, the sync effect preserves the
 * streaming message even if server refetches (to avoid losing partial AI response).
 */

/**
 * Transforms raw GraphQL stream edges to local ConversationMessage format.
 * Sorts by creation date (oldest first).
 */
function transformServerMessages(
  edges: AIConversation['aiConversation']['streams']['edges'],
): ConversationMessage[] {
  return edges
    .map(edge => ({
      streamId: edge.node.streamId,
      userPrompt: edge.node.userPrompt || '',
      aiResponse: edge.node.responseText || edge.node.error || '',
      usage: edge.node.usage,
      riskScoreEvidence: edge.node.riskScoreEvidence,
      toolInvocations: edge.node.toolInvocations ?? [],
      finished: edge.node.finished,
      createdAt: edge.node.createdAt,
    }))
    .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
}

export const STREAMS_PAGE_SIZE = 10;

export const useConversationMessagingV3 = ({
  conversationId,
  isPrivate,
  onConversationCreated,
  alias,
  kind,
  events,
  onStreamComplete,
  autoScrollBottom = true,
}: UseConversationMessagingV3Props): UseConversationMessagingV3Return => {
  const { pushSnackbar } = useSnackbar();

  // Fetch conversation history
  const { data, loading, error, fetchMore, refetch } = useAIConversation({
    variables: {
      id: conversationId ?? '',
      streamsInput: {
        pageSize: STREAMS_PAGE_SIZE,
      },
    },
    skip: !conversationId,
    // Use cache-and-network to show cached data immediately while fetching fresh data.
    // This prevents flash of empty state when switching between conversations.
    // Extra network request on navigation, but better UX.
    fetchPolicy: 'cache-and-network',
  });

  const conversation = data?.aiConversation ?? null;

  // Mutations for creating/continuing conversations
  const [createMutation, { loading: isCreating }] = useCreateAIConversation();
  const [continueMutation, { loading: isContinuing }] = useContinueAIConversation();

  // Local state for message management
  const [activeStreamId, setActiveStreamId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<ConversationMessage[]>([]);
  const [forceScrollToBottom, setForceScrollToBottom] = React.useState(0);

  // Track current conversationId to maintain correct state when switching conversations
  const currentConversationIdRef = React.useRef(conversationId);
  currentConversationIdRef.current = conversationId;

  // Track current activeStreamId to prevent stale closures in callbacks
  const activeStreamIdRef = React.useRef(activeStreamId);
  activeStreamIdRef.current = activeStreamId;

  // Stabilize onStreamComplete to avoid re-creating useAIStreamManagementV3 callbacks
  const onStreamCompleteRef = React.useRef(onStreamComplete);
  onStreamCompleteRef.current = onStreamComplete;

  // Stabilize events to avoid re-creating handleSendMessage on every parent render.
  // Events are only used during conversation creation, not as a reactive dependency.
  const eventsRef = React.useRef(events);
  eventsRef.current = events;

  // Capture the conversation ID at stream-start time so completion callbacks can guard against
  // stale closures: if Apollo's onCompleted ref picks up a re-rendered callback that already
  // closes over the new conversationId, comparing currentConversationIdRef.current against that
  // closed-over value would always be equal and the guard would never fire.
  const streamConversationIdRef = React.useRef<string | null>(null);

  // Derived state
  const isInitialLoad = loading && !conversation;

  // isLoadingMore tracks only paginated fetchMore calls, not general refetches.
  // Using `loading && !!conversation` would produce false positives because refetch()
  // is called after stream completion — that loading cycle is not a pagination event.
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  // Ref to guard against concurrent fetchMore calls before the state update settles.
  const isFetchingMoreRef = React.useRef(false);
  // Tracks whether the user has loaded pages beyond the first.
  // endlessPaginationDefaultMerge resets to page 1 when refetch() is called with no cursor,
  // so we skip the post-stream refetch if the user has paginated to avoid wiping their
  // scroll history. The trade-off is that riskScoreEvidence for the latest message will
  // be stale until the next full conversation load.
  const hasLoadedMultiplePagesRef = React.useRef(false);

  // Runs when server data changes (refetch, mutation complete, cache update).
  // Reads activeStreamIdRef (not state) to avoid re-running on stream state transitions.
  // This prevents a race where activeStreamId→null triggers this effect before refetch()
  // resolves, briefly overwriting messages with stale server data.
  React.useEffect(() => {
    if (!conversation?.streams?.edges) {
      // No server data yet - only clear if NOT streaming (preserve optimistic message)
      if (!activeStreamIdRef.current) {
        setMessages([]);
      }
      return;
    }

    const serverMessages = transformServerMessages(conversation.streams.edges);

    setMessages(prev => {
      // Actively streaming - preserve in-progress message at end
      if (activeStreamIdRef.current) {
        const streamingMessage = prev.find(m => m.streamId === activeStreamIdRef.current);
        if (streamingMessage) {
          return [
            ...serverMessages.filter(m => m.streamId !== activeStreamIdRef.current),
            streamingMessage,
          ];
        }
      }
      // No active stream — use server as source of truth, but fall back to local userPrompt
      // for conversations created before the prompt-display fix (server stored empty userPrompt).
      const localByStreamId = new Map(prev.map(m => [m.streamId, m]));
      return serverMessages.map(serverMsg => ({
        ...serverMsg,
        userPrompt:
          serverMsg.userPrompt || localByStreamId.get(serverMsg.streamId)?.userPrompt || '',
      }));
    });
    // activeStreamId intentionally read from ref to avoid re-running on stream state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.streams?.edges]);

  // Reconnect to in-progress streams on navigation return.
  // When the component remounts after navigation, conversation data loads via
  // cache-and-network but activeStreamId is reset to null. If the server still
  // has a stream with finished=false, we resume polling here so the user sees
  // the in-progress or completed response when they return.
  React.useEffect(() => {
    // Already polling — no reconnect needed
    if (activeStreamIdRef.current) {
      return;
    }

    const edges = conversation?.streams?.edges;
    if (!edges?.length) {
      return;
    }

    // Find the most recent unfinished stream (server-side still processing)
    const unfinishedEdge = [...edges]
      .filter(edge => !edge.node.finished && !edge.node.error)
      .sort((a, b) => dayjs(b.node.createdAt).valueOf() - dayjs(a.node.createdAt).valueOf())[0];

    if (unfinishedEdge) {
      streamConversationIdRef.current = conversationId;
      setActiveStreamId(unfinishedEdge.node.streamId);
    }
  }, [conversation?.streams?.edges, conversationId]);

  // Stream management - integrated directly
  const {
    aiSummary,
    aiSummaryError,
    initialUserPrompt: currentUserPrompt,
    isLoading: isStreaming,
    cancelStream: cancelAIStream,
    resetStream,
    toolInvocations: streamingToolInvocations,
    isCancelled,
    riskScoreEvidence: streamRiskScoreEvidence,
  } = useAIStreamManagementV3({
    streamId: activeStreamId ?? undefined,
    onStreamComplete: (summary, _prompt, streamUsage, riskEvidence) => {
      // Only update if still on same conversation.
      // Use streamConversationIdRef (set at stream-start time) rather than closing over
      // conversationId: Apollo's onCompleted ref always calls the latest callback, so the
      // closed-over conversationId would already reflect the new conversation, defeating the guard.
      if (currentConversationIdRef.current !== streamConversationIdRef.current) {
        return;
      }

      // Use ref to get current streamId
      const streamId = activeStreamIdRef.current;
      if (!streamId) {
        return;
      }

      // Update the streaming message to finished, including riskScoreEvidence atomically
      // so it's available before the typewriter animation completes
      setMessages(prev =>
        prev.map(msg =>
          msg.streamId === streamId
            ? {
                ...msg,
                aiResponse: summary,
                usage: streamUsage,
                finished: true,
                riskScoreEvidence: riskEvidence ?? null,
              }
            : msg,
        ),
      );
      setActiveStreamId(null);
      resetStream();
      onStreamCompleteRef.current?.();
      // Refetch to sync server metadata (e.g. riskScoreEvidence) after the stream settles.
      // Skipped if the user has paginated: a no-cursor refetch resets endlessPaginationDefaultMerge
      // to page 1, discarding any scroll history the user has loaded.
      if (!hasLoadedMultiplePagesRef.current) {
        refetch();
      }
    },
    onStreamError: errorMessage => {
      // Same stale-closure guard as onStreamComplete above.
      if (currentConversationIdRef.current !== streamConversationIdRef.current) {
        return;
      }

      // Use ref to get current streamId
      const streamId = activeStreamIdRef.current;
      if (!streamId) {
        return;
      }

      // Update the streaming message with error
      setMessages(prev =>
        prev.map(msg =>
          msg.streamId === streamId
            ? { ...msg, aiResponse: errorMessage, usage: null, finished: true }
            : msg,
        ),
      );
      setActiveStreamId(null);
      resetStream();
      onStreamCompleteRef.current?.();
      if (!hasLoadedMultiplePagesRef.current) {
        refetch();
      }
    },
  });

  // Stabilize streamingToolInvocations reference — Apollo returns a new array object on every
  // poll tick even when the content hasn't changed. Using a ref keeps the message update effect
  // stable. The key below provides a primitive dep that only changes when invocation IDs or
  // decision state actually changes, so the effect fires when a tool invocation first appears
  // even if the response text hasn't changed (e.g. AI is paused waiting for approval).
  const streamingToolInvocationsRef = React.useRef(streamingToolInvocations);
  streamingToolInvocationsRef.current = streamingToolInvocations;
  const streamingToolInvocationsKey = React.useMemo(
    () =>
      activeStreamId
        ? streamingToolInvocations
            .map(t => `${t.id}:${t.approvedAt ?? ''}:${t.rejectedAt ?? ''}:${t.timedOutAt ?? ''}`)
            .join(',')
        : '',
    [activeStreamId, streamingToolInvocations],
  );

  // Same stabilization for riskScoreEvidence — Apollo returns a new object on every poll tick.
  const streamRiskScoreEvidenceRef = React.useRef(streamRiskScoreEvidence);
  streamRiskScoreEvidenceRef.current = streamRiskScoreEvidence;

  // Updates the active streaming message as new content arrives from useAIStreamManagement
  React.useEffect(() => {
    if (
      activeStreamId &&
      (aiSummary || aiSummaryError || streamingToolInvocationsRef.current.length > 0)
    ) {
      // Only update if still on same conversation
      if (currentConversationIdRef.current !== conversationId) {
        return;
      }

      const currentResponse = aiSummary || aiSummaryError;
      setMessages(prev =>
        prev.map(msg =>
          msg.streamId === activeStreamId
            ? {
                ...msg,
                aiResponse: currentResponse,
                userPrompt: currentUserPrompt || msg.userPrompt,
                toolInvocations: streamingToolInvocationsRef.current,
                riskScoreEvidence: streamRiskScoreEvidenceRef.current ?? msg.riskScoreEvidence,
              }
            : msg,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStreamId,
    aiSummary,
    aiSummaryError,
    currentUserPrompt,
    conversationId,
    streamingToolInvocationsKey,
  ]);

  // Pagination
  const hasMore = conversation?.streams?.pageInfo?.hasNextPage ?? false;
  const endCursor = conversation?.streams?.pageInfo?.endCursor;

  const loadMore = React.useCallback(() => {
    // `loading` (Apollo's general flag) is intentionally excluded from this guard.
    // When data hasn't loaded yet, `!hasMore` catches it (conversation is null → false).
    // Including `loading` would cause loadMore to be recreated on every background
    // cache-and-network refetch, which in turn reconnects the IntersectionObserver in
    // MessageList on every background fetch — wasteful and prone to missed events.
    if (!hasMore || !endCursor || isFetchingMoreRef.current) {
      return;
    }

    isFetchingMoreRef.current = true;
    hasLoadedMultiplePagesRef.current = true;
    setIsLoadingMore(true);

    fetchMore({
      variables: {
        streamsInput: {
          pageSize: STREAMS_PAGE_SIZE,
          cursor: endCursor,
        },
      },
    })
      .catch(() => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to load more messages',
          description: 'Check your connection and scroll up to try again.',
        });
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, [hasMore, endCursor, fetchMore, pushSnackbar]);

  // Cancel stream — invoked only on explicit user actions (stop button, "new prompt",
  // delete conversation). Fires the cancelAIInferenceStream mutation here, not in
  // cancelAIStream(), because cancelAIStream is also called from non-user paths
  // (unmount cleanup, conversation switch) where we want the Lambda to keep running
  // so the user can return to it.
  const [cancelAIInferenceStream] = useCancelAIInferenceStream();
  const cancelStream = React.useCallback(() => {
    const streamId = activeStreamIdRef.current;
    if (streamId) {
      cancelAIInferenceStream({ variables: { input: { streamId } } }).catch(() => {
        // best-effort: UI is already in the stopped state
      });
    }
    cancelAIStream();
    // Preserve the current message content before clearing activeStreamId
    // to prevent the sync effect from overwriting with stale server data
    if (streamId) {
      setMessages(prev =>
        prev.map(msg => (msg.streamId === streamId ? { ...msg, finished: true } : msg)),
      );
    }
    setActiveStreamId(null);
    resetStream();
  }, [cancelAIStream, resetStream, cancelAIInferenceStream]);

  const reset = React.useCallback(() => {
    setMessages([]);
    setActiveStreamId(null);
    setIsLoadingMore(false);
    isFetchingMoreRef.current = false;
    hasLoadedMultiplePagesRef.current = false;
  }, []);

  // Cancel active streams when component unmounts
  React.useEffect(() => {
    return () => {
      if (activeStreamIdRef.current) {
        cancelAIStream();
      }
    };
  }, [cancelAIStream]);

  // Conversation switching cleanup
  // Cancel active streams and auto-scroll when user navigates between conversations
  const prevConversationIdRef = React.useRef(conversationId);
  React.useEffect(() => {
    const conversationChanged = prevConversationIdRef.current !== conversationId;
    if (!conversationChanged) {
      return;
    }

    // Clean up any active stream from previous conversation.
    // Uses lower-level functions instead of cancelStream() to avoid a redundant
    // refetch() — Apollo already re-fetches when conversationId variables change.
    if (prevConversationIdRef.current) {
      cancelAIStream();
      resetStream();
      setActiveStreamId(null);
    }

    if (!conversationId) {
      setMessages([]);
    }

    // Reset pagination state so the new conversation starts clean
    setIsLoadingMore(false);
    isFetchingMoreRef.current = false;
    hasLoadedMultiplePagesRef.current = false;

    // Auto-scroll to bottom when navigating to a conversation (but not on initial mount)
    const isNavigatingToConversation = Boolean(conversationId);
    if (isNavigatingToConversation && autoScrollBottom) {
      setForceScrollToBottom(prev => prev + 1);
    }

    prevConversationIdRef.current = conversationId;
  }, [conversationId, cancelAIStream, autoScrollBottom, resetStream]);

  /**
   * Send a message (creates new conversation if needed, or continues existing one).
   *
   * Optimistic update flow:
   * - Immediately add message with tempId (instant UI feedback)
   * - Call mutation (create or continue)
   * - Replace tempId with real streamId from server
   * - Set activeStreamId to start streaming
   * - On error: remove optimistic message, show snackbar
   */
  const handleSendMessage = React.useCallback(
    async (
      userPrompt: string,
      outputLength: OutputLength,
      attachments?: string[],
      displayPrompt?: string,
    ) => {
      if (isCreating || isContinuing || activeStreamId) {
        return;
      }

      // Generate temporary ID for optimistic update
      const tempId = `temp-${uuidv4()}`;

      // Optimistic update: add message immediately
      setMessages(prev => [
        ...prev,
        {
          streamId: tempId,
          userPrompt: displayPrompt || userPrompt,
          aiResponse: '',
          usage: null,
          finished: false,
        },
      ]);
      setForceScrollToBottom(prev => prev + 1);

      try {
        if (!conversationId) {
          // New conversation: create it first
          const result = await createMutation({
            variables: {
              input: {
                userPrompt,
                outputLength,
                private: isPrivate,
                ...(alias ? { alias } : {}),
                ...(kind ? { kind } : {}),
                ...(eventsRef.current?.length ? { events: eventsRef.current } : {}),
                ...(attachments ? { attachments } : {}),
              },
            },
            // Refetch every active AIConversations query so the new conversation
            // shows up in any open list. Trade-off: this re-issues each observer's
            // original (no-cursor) query, which the `endlessPaginationDefaultMerge`
            // policy collapses back to page 1 — users who have deep-scrolled the
            // thread list will lose their accumulated pages here. Acceptable
            // because (a) most users don't deep-scroll and then create a chat,
            // and (b) the alternative (manual cache.modify across every slot)
            // duplicates schema knowledge and is fragile.
            refetchQueries: ['AIConversations'],
          });

          if (!result.data) {
            throw new Error('No data returned from createAIConversation');
          }

          const { conversation: newConversation, streamId } = result.data.createAIConversation;
          onConversationCreated(newConversation.id);

          if (streamId) {
            // Replace temp message with real stream ID
            setMessages(prev =>
              prev.map(msg => (msg.streamId === tempId ? { ...msg, streamId } : msg)),
            );
            // Capture conversation at stream-start time for stale-closure guard in callbacks.
            // Use newConversation.id (not conversationId) because conversationId is still null
            // here — the state update from onConversationCreated hasn't flushed yet.
            streamConversationIdRef.current = newConversation.id;
            // Start streaming
            setActiveStreamId(streamId);
          } else {
            // Failed to start streaming (conversation created but stream initialization failed)
            pushSnackbar({
              variant: 'error',
              title: 'Failed to start AI response',
              description: 'The conversation was created but streaming failed to initialize.',
            });
            // Remove optimistic message on failure
            setMessages(prev => prev.filter(msg => msg.streamId !== tempId));
          }
        } else {
          // Existing conversation: continue it
          const result = await continueMutation({
            variables: {
              input: {
                conversationId,
                userPrompt,
                outputLength,
                ...(attachments ? { attachments } : {}),
              },
            },
          });

          if (!result.data) {
            throw new Error('No data returned from continueAIConversation');
          }

          const { streamId } = result.data.continueAIConversation;

          if (streamId) {
            // Replace temp message with real stream ID
            setMessages(prev =>
              prev.map(msg => (msg.streamId === tempId ? { ...msg, streamId } : msg)),
            );
            streamConversationIdRef.current = conversationId;
            setActiveStreamId(streamId);
          } else {
            // Failed to start streaming
            pushSnackbar({
              variant: 'error',
              title: 'Failed to send message',
              description: 'Your message was saved but AI response failed to initialize.',
            });
            // Remove optimistic message and refetch
            setMessages(prev => prev.filter(msg => msg.streamId !== tempId));
            refetch();
          }
        }
      } catch (err) {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to send message',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        });
        // Remove optimistic message on error
        setMessages(prev => prev.filter(msg => msg.streamId !== tempId));
      }
    },
    [
      conversationId,
      isPrivate,
      isCreating,
      isContinuing,
      activeStreamId,
      createMutation,
      continueMutation,
      onConversationCreated,
      pushSnackbar,
      refetch,
      alias,
      kind,
    ],
  );

  return {
    conversation,
    messages,
    hasMore,
    loadMore,
    isStreaming,
    isCreating,
    isContinuing,
    isInitialLoad,
    isLoadingMore,
    currentUserPrompt,
    isCancelled,
    handleSendMessage,
    cancelStream,
    reset,
    forceScrollToBottom,
    error: error ?? null,
  };
};
